import React, {ComponentType, useEffect} from 'react'
import {connect} from '@modifi/redux'
import {getComponentDisplayName} from '@modifi/utilities'

import {selectStatesByCountryCode, aFetchStatesByCountryCode} from '../../data/states'
import {AppState} from '../../types/redux'
import {SelectOption} from '../../types/common'

/** Props the HOC injects into the wrapped component. */
export type WithStatesOptionsProps = {
  options?: SelectOption[]
}

type OwnProps = {
  countryCode: string
}

type ScopedComponentProps = OwnProps &
  WithStatesOptionsProps & {
    ensureCountries: (countryCode: string) => void
  }

const selectStateOptions = (state: AppState, countryCode: string): SelectOption[] | undefined =>
  selectStatesByCountryCode(state, countryCode)?.map(({name, stateCode}) => ({
    label: name?.en ?? '',
    value: stateCode ?? '',
  }))

const withStateOptions = (WrappedComponent: ComponentType<WithStatesOptionsProps>) => {
  const ScopedComponent = ({ensureCountries, countryCode, ...props}: ScopedComponentProps) => {
    useEffect(() => {
      countryCode && ensureCountries(countryCode)
    }, [ensureCountries, countryCode])
    return <WrappedComponent {...props} />
  }

  const mapStateToProps = (state: AppState, ownProps: OwnProps) => ({
    options: selectStateOptions(state, ownProps?.countryCode),
  })

  const mapDispatchToProps = {
    ensureCountries: aFetchStatesByCountryCode,
  }

  ScopedComponent.WrappedComponent = WrappedComponent
  ScopedComponent.displayName = `withStatesOptions(${getComponentDisplayName(WrappedComponent)})`

  return connect(mapStateToProps, mapDispatchToProps)(ScopedComponent)
}

export default withStateOptions
